import { Injector } from '@angular/core';
import { ObjectUtil } from '../../../../components/common/object-util/object-util';
import { ReportDesignerPageComponent } from '../../components/report-designer-page/report-designer-page.component';
import { ReportDesignerPage } from '../../models/report-designer-page';
import { ReportDesignerPrintSettings } from '../../models/report-designer-print-settings';
import { ReportDesignerHostComponent } from '../../report-designer-host/report-designer-host.component';
import { PausePrintRequest } from './models/pause-print-request';
import { PrintServiceBase } from "./print-service-base";
import { PrintService } from './print.service';

/**
 * Batched printing print service
 */
export class PagedPrintBatchedService extends PrintServiceBase implements PrintService {
  /**
   * Page waits for print?
   */
  readonly pageWaitsForPrint = true;

  /**
   * Raise CSS print events?
   */
  readonly raiseCssPrintEvents = false;

  /**
   * Pause after n milliseconds, so the function doesn't time out
   */
  private pauseAfterMs = 3.5 * 60 * 1000;

  /**
   * Pause timer ID
   */
  private pauseTimerId;

  /**
   * Batch pages count
   */
  private readonly batchPagesCount: number;

  /**
   * Constructor
   * @param batchPagesCount
   * @param printSettings
   * @param injector
   * @param window
   */
  constructor(batchPagesCount: number
    , printSettings: ReportDesignerPrintSettings
    , injector: Injector
    , window: Window) {
    super(printSettings, injector, window);
    this.batchPagesCount = batchPagesCount;
  }

  /**
   * Initialization logic
   * @param host
   */
  init(host: ReportDesignerHostComponent): void {
    if (this.rdStorage.getAppPrintMode()) {
      this.host = host;
      this.document = this.rdStorage.getActiveDocument();
      this.pages = this.document.data.pages;

      //mark all pages waiting for print
      for (const page of this.pages) {
        page.options.isOnDisplay = true;
        page.options.waitingForPrint = true;

        //mark page loaded if no widgets or hidden
        page.dataLoaded = page.widgets.length === 0 || page.options.isHidden;
      }

      const pages = this.getPrintablePages();
      if (pages.length === 0) {
        //nothing to print => inform app/puppeteer that there is nothing to print
        this.pdfExportEnded();
      } else {
        this.puppeteerExportStarted();

        this.handlePrintResume().then(() => {
          this.prepareDocumentForPrint();

          this.hashtagService.getAllHashtags();
          this.reportDataPreloaderService.preloadData(pages).then(() => {
            //start print process by displaying first batch
            this.batchPagesForPrint();
          });

          //attach pause handler
          this.handlePause(this.pauseAfterMs);
        }).catch(() => {
          this.handlePrintError();
        });
      }
    }
  }

  /**
   * Page data loaded callback
   * @param pageComponent
   */
  public pageDataLoaded(pageComponent: ReportDesignerPageComponent): void {
    const pageIndex = this.getPageIndex(pageComponent.page);
    const alreadyUploaded = this.uploadedPages.filter(p => p.pageIndex === pageIndex).length !== 0;
    if (alreadyUploaded) {
      //page could already be uploaded if print resume is in progress
      this.processedPagesIndexes.push(pageIndex);
      this.markProcessedPagesLoaded();//mark eventual already printed slave pages loaded
      this.processNextPage(pageComponent);
    } else {
      setTimeout(() => {
        this.readyToProcessQueue.enqueue(pageComponent);
        this.processQueue();
      }, this.PROCESS_AFTER_LOADED_TIMEOUT);
    }
  }

  /**
   * Process the queue
   */
  protected processQueue(): void {
    const shouldProcessPage = !this.errorOccurred
      && this.readyToProcessQueue.size > 0
      && !this.pauseRequested;

    if (shouldProcessPage) {
      const pageComponent = this.readyToProcessQueue.dequeue();
      this.processingQueue.enqueue(pageComponent);
      this.waitForReadyToPrint(pageComponent).then(() => {
        if (!this.pauseRequested) {
          this.printPage(pageComponent);
        }
      });
    }
  }

  /**
   * Batch pages for printing
   */
  protected batchPagesForPrint(): void {
    const pages = this.getPrintablePages();
    //get count of printing pages
    const printInProgressPages = pages.filter(p => !p.options.waitingForPrint);
    const masterPageIsPrinting = !!printInProgressPages.find(p => p.isVirtualPageMaster);
    if (!masterPageIsPrinting) {
      const printInProgressPagesCount = printInProgressPages.length;
      let pagesCountToBatch = this.batchPagesCount - printInProgressPagesCount;
      if (pagesCountToBatch > 0) {
        const waitingForPrintPages = pages.filter((page, pageIndex) =>
          this.processedPagesIndexes.indexOf(pageIndex) === -1
          && page.options.waitingForPrint
          && !this.readyToProcessQueue.contains(p => p.page === page)//not in readyToProcessQueue
          && !this.processingQueue.contains(p => p.page === page)//not in processingQueue
        );

        for (const page of waitingForPrintPages) {
          page.options.waitingForPrint = false;
          this.eventBus.broadcastEvent('pagedPrint.pagePrintingStart', page);//required for empty pages to fire pageDataLoaded
          pagesCountToBatch--;
          if (pagesCountToBatch === 0 || page.isVirtualPageMaster) {
            break;
          }
        }
      }
    }
  }

  /**
   * Document data loaded 
   */
  public documentDataLoaded(): void {
    //prevent multiple calls
    if (!this.documentDataLoadedProcessed) {
      this.documentDataLoadedProcessed = true;
      this.handleDocumentDataLoaded();
    }
  }

  /**
   * last page was loaded (and probably processing right now)
   */
  private handleDocumentDataLoaded(): void {
    /******************************
      // last page was loaded (and probably processing right now)
      // wait until:
      // a) process queue is empty and processedPageComponents lenght is same as printablePages length
      // OR
      //b) error occurred
      *******************************/
    let WAITING_TIME = 30000;
    const CHECK_INTERVAL = 100;
    const intervalId = setInterval(() => {
      WAITING_TIME -= CHECK_INTERVAL;

      const allProcessed = this.readyToProcessQueue.size === 0 && this.processingQueue.size === 0
        && this.uploadedPages.length === this.getPrintablePages().length;

      if (allProcessed) {
        this.disablePause();
        this.log(`All processed.. Start publish...`, '\n', '\n\n', 'color: green');
        if (!this.fakeUpload) {
          this.pdfCreated();
        }
      }

      if (allProcessed || this.errorOccurred || WAITING_TIME < 0 || this.pauseRequested) {
        clearInterval(intervalId);
      }

      if (this.errorOccurred || WAITING_TIME < 0) {
        this.handlePrintError();
      }

    }, CHECK_INTERVAL);
  }

  /**
   * Handle pausing
   * @param pauseAfterMs
   */
  private handlePause(pauseAfterMs: number) {
    this.pauseTimerId = setTimeout(() => {
      this.handlePrintPause().then((paused) => {
        if (paused) {
          this.puppeteerLogMessage('Print paused..');
          this.puppeteerExportEnded();
        }
      });

    }, pauseAfterMs);
  }

  /**
   * Disable pausing
   */
  private disablePause() {
    if (this.pauseTimerId) {
      clearTimeout(this.pauseTimerId);
    }
  }

  /**
   * Handle prit pausing
   * @returns
   */
  private async handlePrintPause(): Promise<boolean> {
    if (this.mergeInProgress) {
      //do nothing, we'are assuming that merge will end quicker than 30 sec
      return false;
    } else {
      try {
        this.uploadedPages.sort((a, b) => a.pageIndex - b.pageIndex);//Array is mutated at search, so let's make this more clear
        if (this.uploadedPages.length > 0) {

          this.pauseRequested = true;
          this.readyToProcessQueue.clear();
          this.processingQueue.clear();

          const pausePrintRequest: PausePrintRequest = {
            printGuid: this.printGuid,
            uploadedPages: this.uploadedPages,
            virtualPagesPrintInProgress: false
          };

          const pages = this.getPrintablePages();
          const lastUploadedPage = this.uploadedPages[this.uploadedPages.length - 1];
          const isVirtual = lastUploadedPage.isVirtualPageMaster || lastUploadedPage.isVirtualPage;
          if (isVirtual && pages.length > lastUploadedPage.pageIndex) {
            const nextPageIndex = lastUploadedPage.pageIndex + 1;
            const isNextPageVirtualPage = pages[nextPageIndex].isVirtualPage;
            pausePrintRequest.virtualPagesPrintInProgress = isNextPageVirtualPage;
          }

          const gpr = await this.pausePrint(this.host.PdfExportId, pausePrintRequest);
          return gpr.success;
        } else {
          return false;
        }
      } catch {
        return false;
      }
    }
  }

  /**
   * Handle print resuming
   * @returns
   */
  private async handlePrintResume(): Promise<void> {
    if (!this.printSettings.pausedState) {
      //not in paused state => return
      return;
    }

    const state = this.printSettings.pausedState as PausePrintRequest;

    //keep the original print guid!
    this.printGuid = state.printGuid;

    //restore uploadedPages and processedPagesIndexes
    state.uploadedPages.sort((a, b) => a.pageIndex - b.pageIndex); //Array is mutated at search, so let's make this more clear
    this.uploadedPages = state.uploadedPages;
    this.processedPagesIndexes = this.uploadedPages.map(p => p.pageIndex);

    const lastMasterPageIndex = this.uploadedPages.filter(p => p.isVirtualPageMaster).pop()?.pageIndex || -1;

    const recreateVirtualPagesUpToIndex = state.virtualPagesPrintInProgress ? lastMasterPageIndex : this.processedPagesIndexes.length;

    //we need to restore virtual pages if there was some
    for (const [idx, uploadedPage] of this.uploadedPages.entries()) {
      if (uploadedPage.isVirtualPage && idx < recreateVirtualPagesUpToIndex) {
        //this master-slave pages where already processed (as there is some other master page after this one (for this slave pages)
        //we need to add the page at position idx
        const page: ReportDesignerPage = ObjectUtil.deepCopy(this.widgetService.getHPageConfig());
        page.isVirtualPage = true;
        page.dataLoaded = true;
        page.options.isOnDisplay = true;
        page.options.waitingForPrint = true;

        this.document.addPage(page, idx, false);
      }
    }

    if (lastMasterPageIndex !== -1 && state.virtualPagesPrintInProgress) {
      //if virtual pages was in print we need to restore master page to get data for slave pages
      //remove its index from processedPagesIndexes so it will be processed again by batchPagesForPrint
      //master page will create slave pages automatically
      const idx = this.processedPagesIndexes.indexOf(lastMasterPageIndex);
      if (idx !== -1) {
        this.processedPagesIndexes.splice(idx, 1);
      }
    }

    //finally we have to set pageDataLoaded for all pages that were printed before this resume
    //but up to lastMasterPageIndex if virtual pages print is in progress
    //(virtual pages are not yet in pages array, so not mark loaded pages that might come after virtual ones)
    const maxIndex = state.virtualPagesPrintInProgress ? lastMasterPageIndex : null;
    this.markProcessedPagesLoaded(maxIndex);

    //reset pause state
    this.printSettings.pausedState = null;

    const gpr = await this.resumePrint(this.host.PdfExportId);
    if (!gpr.success) {
      throw new Error('Failed to resume print!');
    }
  }

  /**
   * Mark processed pages as loaded
   * @param maxIndex
   */
  private markProcessedPagesLoaded(maxIndex: number | null = null) {
    const pages = this.getPrintablePages();
    const toIndex = maxIndex ?? pages.length;

    for (let pageIndex = 0; pageIndex < toIndex; pageIndex++) {
      if (this.processedPagesIndexes.indexOf(pageIndex) !== -1) {
        const page = pages[pageIndex];
        page.dataLoaded = true;
      }
    }
  }
}
